'use es6';

/**
 * Fork of UIComponents/nav/UINavListItem
 * https://git.hubteam.com/HubSpot/UIComponents/blob/40ff2a1/UIComponents/static-3.2965/js/nav/UINavListItem.js
 */
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["linkComponent", "active", "hasChildren", "hovered"],
  _excluded2 = ["hasChildren"],
  _excluded3 = ["active", "children", "className", "count", "duration", "hovered", "iconLeft", "iconRight", "itemClassName", "linkComponent", "onBlur", "onClick", "onOpenChange", "open", "_rootRenderer", "title", "value"],
  _excluded4 = ["hovered"];
import PropTypes from 'prop-types';
import { isValidElement, Component } from 'react';
import styled, { css } from 'styled-components';
import AccordionTransition from 'UIComponents/transitions/AccordionTransition';
import classNames from 'classnames';
import Controllable from 'UIComponents/decorators/Controllable';
import HoverProvider from 'UIComponents/providers/HoverProvider';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILink from 'UIComponents/link/UILink';
import NavItemLabel from './NavItemLabel';
import { hidden } from 'UIComponents/utils/propTypes/decorators';
import { isRenderable } from 'UIComponents/utils/propTypes/componentProp';
import { NAV_LIST_TRANSITION_ANIMATION_TIMING } from 'HubStyleTokens/times';
import { BASE_FONT_SIZE, MERCURY_LAYER, NAV_ITEM_PADDING_LEFT, NAV_ITEM_PADDING_RIGHT, NAV_ITEM_PADDING_Y, NAV_ITEM_INNER_SPACING_X } from 'HubStyleTokens/sizes';
import { setFontSmoothing, setBorderRadius, setFontSize, FONT_FAMILIES } from 'UIComponents/utils/Styles';
import { CALYPSO } from 'HubStyleTokens/colors';
import { BASE_LINK_COLOR, SIDENAV_FOREGROUND_COLOR, SIDENAV_HOVERED_BACKGROUND_COLOR, SIDENAV_SELECTED_BACKGROUND_COLOR, BASE_FONT_COLOR } from 'HubStyleTokens/theme';
import { WEB_FONT_DEMI_BOLD_WEIGHT } from 'HubStyleTokens/misc';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ICON_SIZE = 'xxs';
const NavListItemTransitionWrapper = styled(AccordionTransition.defaultProps.Wrapper).withConfig({
  displayName: "NavListItem__NavListItemTransitionWrapper",
  componentId: "sc-1dtujhy-0"
})(["margin-left:-", ";padding-left:", ";overflow:", ";"], NAV_ITEM_PADDING_LEFT, NAV_ITEM_PADDING_LEFT, ({
  transitioning
}) => transitioning ? 'hidden' : 'visible');
const activeStyles = css(["color:", ";background-color:", ";font-weight:", ";", ";"], BASE_FONT_COLOR, SIDENAV_SELECTED_BACKGROUND_COLOR, WEB_FONT_DEMI_BOLD_WEIGHT, setBorderRadius());
const hoveredStyles = css(["", ";color:", ";text-decoration:none;background-color:", ";"], setBorderRadius(), BASE_FONT_COLOR, SIDENAV_HOVERED_BACKGROUND_COLOR);
const parentStyles = css(["user-select:none;"]);
const StyledLink = styled(props => {
  const {
      linkComponent: Link
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  return /*#__PURE__*/_jsx(Link, Object.assign({}, rest));
}).withConfig({
  displayName: "NavListItem__StyledLink",
  componentId: "sc-1dtujhy-1"
})(["display:flex;", ";", ";", ";color:", ";left:-", ";line-height:16px;padding:", " ", " ", " ", ";position:relative;text-decoration:none;width:calc(100% + ", ");cursor:", ";", " &&{", "}", " &.nav-item--active{", ";}&:focus{z-index:", ";}"], setFontSmoothing('auto'), FONT_FAMILIES.default, setFontSize(BASE_FONT_SIZE), SIDENAV_FOREGROUND_COLOR, NAV_ITEM_PADDING_LEFT, NAV_ITEM_PADDING_Y, NAV_ITEM_PADDING_RIGHT, NAV_ITEM_PADDING_Y, NAV_ITEM_PADDING_LEFT, NAV_ITEM_PADDING_LEFT, ({
  disabled
}) => disabled === true ? 'not-allowed' : 'pointer', ({
  active
}) => active ? activeStyles : undefined, ({
  hovered,
  disabled
}) => hovered && !disabled ? hoveredStyles : undefined, ({
  hasChildren
}) => hasChildren ? parentStyles : undefined, activeStyles, MERCURY_LAYER);
const Icon = styled(props => {
  const rest = _objectWithoutPropertiesLoose(props, _excluded2);
  return /*#__PURE__*/_jsx(UIIcon, Object.assign({}, rest));
}).withConfig({
  displayName: "NavListItem__Icon",
  componentId: "sc-1dtujhy-2"
})(["margin-left:", ";margin-right:", ";&&&{margin-right:", ";color:", ";}"], ({
  placement
}) => placement === 'left' && '0', NAV_ITEM_INNER_SPACING_X, ({
  placement
}) => placement === 'right' && '0 !important', ({
  hasChildren,
  placement
}) => `${hasChildren && placement === 'right' ? BASE_LINK_COLOR : CALYPSO} !important}`);
const StyledNavItemLabel = styled(NavItemLabel).withConfig({
  displayName: "NavListItem__StyledNavItemLabel",
  componentId: "sc-1dtujhy-3"
})(["flex-grow:1;"]);
class NavListItem extends Component {
  constructor(...args) {
    super(...args);
    this.handleClick = evt => {
      const {
        onClick,
        onOpenChange,
        open
      } = this.props;
      if (onClick) onClick(evt);
      if (!evt.defaultPrevented) {
        onOpenChange(SyntheticEvent(!open));
      }
    };
  }
  render() {
    const _this$props = this.props,
      {
        active,
        children,
        className,
        count,
        duration,
        iconLeft,
        iconRight,
        itemClassName,
        linkComponent,
        open,
        _rootRenderer,
        title
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded3);
    const renderIcon = (icon, placement) => {
      if (!icon) return null;
      if ( /*#__PURE__*/isValidElement(icon)) {
        return icon;
      }
      if (typeof icon === 'function') {
        return icon({
          children,
          open,
          size: ICON_SIZE,
          placement
        });
      }
      return /*#__PURE__*/_jsx(Icon, {
        name: icon,
        size: ICON_SIZE,
        placement: placement,
        hasChildren: children !== null
      });
    };
    return /*#__PURE__*/_jsx(HoverProvider, Object.assign({}, this.props, {
      children: hoverProviderProps => {
        const {
            hovered
          } = hoverProviderProps,
          hoverProviderRestProps = _objectWithoutPropertiesLoose(hoverProviderProps, _excluded4);
        const classes = classNames(className, 'nav-item', active && 'nav-item--active');
        return /*#__PURE__*/_jsxs("li", {
          className: itemClassName,
          children: [/*#__PURE__*/_jsxs(StyledLink, Object.assign({
            active: active,
            hasChildren: isRenderable(children),
            hovered: hovered || undefined,
            linkComponent: linkComponent
          }, rest, hoverProviderRestProps, {
            "aria-current": active ? 'true' : null,
            className: classes,
            onClick: this.handleClick,
            children: [renderIcon(iconLeft, 'left'), /*#__PURE__*/_jsx(StyledNavItemLabel, {
              count: count,
              children: title
            }), renderIcon(iconRight, 'right')]
          })), /*#__PURE__*/_jsx(AccordionTransition, {
            open: open,
            duration: duration,
            Wrapper: NavListItemTransitionWrapper,
            children: isRenderable(children) && _rootRenderer && _rootRenderer(children)
          })]
        });
      }
    }));
  }
}
NavListItem.propTypes = {
  active: PropTypes.bool.isRequired,
  children: PropTypes.node,
  className: PropTypes.string,
  count: PropTypes.node,
  disabled: PropTypes.bool,
  duration: PropTypes.number,
  hovered: hidden(PropTypes.bool),
  iconLeft: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  iconRight: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  itemClassName: PropTypes.string,
  linkComponent: PropTypes.elementType,
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  onOpenChange: PropTypes.func,
  open: PropTypes.bool,
  title: PropTypes.node.isRequired,
  value: PropTypes.node,
  _rootRenderer: PropTypes.func
};
NavListItem.defaultProps = {
  active: false,
  disabled: false,
  duration: parseInt(NAV_LIST_TRANSITION_ANIMATION_TIMING, 10),
  linkComponent: UILink,
  open: false
};
NavListItem.displayName = 'NavListItem';
export default Controllable(NavListItem, ['open']);